// 🧊 3D 부품 층(React) — 미리 구운 블렌더 부품(색 mp4 + 마스크 mp4)을 캔버스 하나에 그린다(10-01 · 모션 브리핑 3D 층 2-1).
//   ★`<video>` 로 얹지 않는다★ — 브라우저 리모션(web-renderer)은 `<video>` 를 캡처하지 않는다(09-29 0단계 함정). 쇼릴 캔버스와 같은 길:
//   ① 필요한 표본(색·마스크의 그 시각 프레임)을 받아 두고(`fillSamples`) ② 캔버스에 두 벌 합성으로 그린다(`drawMatted*`).
//   굽기(web-renderer·서버 렌더)는 프레임마다 기다렸다 그리고(`useDelayRender` 훅 — 전역 delayRender 는 web-renderer 가 세지 않는다),
//   미리보기는 기다리지 않고 «가장 최근에 요청된 프레임만» 그린다.
//   ★부품을 못 받으면 조용히 비운다★ — 층 하나가 빠질 뿐 렌더는 계속된다(2D 무대가 그대로 남는다 · 검은 프레임·중단 금지).
import React, { useLayoutEffect, useRef } from "react";
import { useCurrentFrame, useDelayRender, useRemotionEnvironment, useVideoConfig } from "remotion";
import { motion3dAsset, motion3dUrl, type Motion3dAsset } from "./assets";
import { drawMaskFill, drawMatted, drawMattedTinted, type Matte2D } from "./matte";
import { fillSamples, sampleKey } from "./sources";

export interface Motion3dLayerProps {
  /** 자산 id(`MOTION_3D_ASSETS`). 모르는 id 면 아무것도 그리지 않는다. */
  assetId: string;
  /** 부품이 재생을 시작하는 프레임(이 컴포지션·시퀀스 기준). */
  at: number;
  /** 무채색 부품(`tint: "neutral"`)에 입힐 색 · 마스크 전용 부품의 채움색. 없으면 색 층 그대로(마스크 전용은 그리지 않음). */
  color?: string;
  /** 층 불투명도(0~1). */
  opacity?: number;
  /** 마스크 전용 부품 — 마스크 밖을 칠한다. */
  invert?: boolean;
}

/** 이 프레임의 부품 안 시각(초) — 재생 창 [0, 길이) 밖이면 null. 순수(렌더 결정성 · 60fps 부품도 그 시각의 프레임을 받는다). */
export function motion3dLocalSec(frame: number, at: number, fps: number, asset: Pick<Motion3dAsset, "durationSec">): number | null {
  if (!(fps > 0)) return null;
  const t = (frame - at) / fps;
  if (t < 0 || t >= asset.durationSec - 1e-6) return null;
  return t;
}

/** 표본으로 한 프레임을 그린다 — 마스크가 없으면 false(층을 비운다 = 2D 대체). 순수(캔버스 호출만). */
export function drawMotion3dFrame(
  ctx: Matte2D, asset: Pick<Motion3dAsset, "matteOnly" | "tint">,
  colorImg: CanvasImageSource | null, matteImg: CanvasImageSource | null,
  W: number, H: number, opts: { color?: string; opacity?: number; invert?: boolean } = {},
): boolean {
  if (!matteImg) return false;
  const alpha = opts.opacity ?? 1;
  if (asset.matteOnly) {
    if (!opts.color) return false;
    ctx.save();
    ctx.globalAlpha *= alpha;
    drawMaskFill(ctx, matteImg, opts.color, W, H, opts.invert === true);
    ctx.restore();
    return true;
  }
  if (!colorImg) return false;
  if (asset.tint === "neutral" && opts.color) drawMattedTinted(ctx, colorImg, matteImg, opts.color, 0, 0, W, H, alpha);
  else drawMatted(ctx, colorImg, matteImg, 0, 0, W, H, alpha);
  return true;
}

export const Motion3dLayer: React.FC<Motion3dLayerProps> = ({ assetId, at, color, opacity, invert }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const ref = useRef<HTMLCanvasElement>(null);
  const { delayRender, continueRender } = useDelayRender();
  const runtime = useRemotionEnvironment();
  const rendering = runtime.isRendering || runtime.isClientSideRendering;
  const latest = useRef(0);
  const busy = useRef(false);
  const asset = motion3dAsset(assetId);

  useLayoutEffect(() => {
    const canvas = ref.current;
    if (!canvas || !asset) return;
    const clear = () => canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    const paint = async (f: number): Promise<{ colorImg: CanvasImageSource | null; matteImg: CanvasImageSource | null } | null> => {
      const t = motion3dLocalSec(f, at, fps, asset);
      if (t === null) return null;
      const matteUrl = motion3dUrl(asset.id, "matte.mp4");
      const colorUrl = motion3dUrl(asset.id, "color.mp4");
      const needs = asset.matteOnly ? [{ url: matteUrl, tSec: t }] : [{ url: colorUrl, tSec: t }, { url: matteUrl, tSec: t }];
      const got = await fillSamples(needs);
      return { colorImg: got.get(sampleKey(colorUrl, t)) ?? null, matteImg: got.get(sampleKey(matteUrl, t)) ?? null };
    };
    const put = (s: { colorImg: CanvasImageSource | null; matteImg: CanvasImageSource | null } | null) => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, width, height);
      if (s) drawMotion3dFrame(ctx, asset, s.colorImg, s.matteImg, width, height, { color, opacity, invert });
    };
    if (motion3dLocalSec(frame, at, fps, asset) === null) { clear(); return; }
    if (!rendering) {
      latest.current = frame;
      if (busy.current) return;
      busy.current = true;
      (async () => {
        let done = -1;
        while (done !== latest.current) {
          const f = latest.current;
          const s = await paint(f);
          if (ref.current !== canvas) break;
          put(s);
          done = f;
        }
      })().catch((e) => { console.warn("[motion-3d] 미리보기 프레임 실패", assetId, e); clear(); }).finally(() => { busy.current = false; });
      return;
    }
    const h = delayRender(`motion-3d ${assetId} f${frame}`);
    let alive = true;
    (async () => {
      const s = await paint(frame);
      if (alive) put(s);
    })()
      .catch((e) => { console.warn("[motion-3d] 프레임 실패 — 층을 비운다(2D 대체)", assetId, frame, e); if (alive) clear(); })
      .finally(() => continueRender(h));
    return () => { alive = false; };
  }, [frame, fps, width, height, asset, assetId, at, color, opacity, invert, rendering, delayRender, continueRender]);

  if (!asset) return null;
  return <canvas ref={ref} data-motion-3d={assetId} width={width} height={height} style={{ position: "absolute", left: 0, top: 0, width, height, pointerEvents: "none" }} />;
};
